/**
 * Shared layout for public legal pages (Privacy Policy, Terms of Use).
 *
 * These routes must remain PUBLIC — no auth required, no redirect to
 * /login. Intuit's app-review process requires that both URLs load
 * anonymously.
 */
import { Link } from "react-router-dom";

export function LegalPage({
  title,
  effectiveDate,
  lastUpdated,
  testid,
  children,
}) {
  return (
    <div
      data-testid={testid}
      className="min-h-screen bg-cream text-brand-primary"
    >
      {/* Header */}
      <header className="border-b border-brand-primary/10 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/"
            data-testid="legal-brand-home-link"
            className="flex items-center gap-3 text-brand-primary hover:opacity-80 transition-opacity"
          >
            <div className="h-9 w-9 rounded-xl bg-terracotta/10 grid place-items-center">
              <span className="font-heading font-semibold text-terracotta text-sm">FC</span>
            </div>
            <div className="leading-tight">
              <p className="font-heading font-semibold text-brand-primary">Family Chapel</p>
              <p className="text-xs text-brand-primary/60">Staff Dashboard</p>
            </div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link
              to="/legal/privacy"
              data-testid="legal-nav-privacy"
              className="text-brand-primary/70 hover:text-terracotta transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms"
              data-testid="legal-nav-terms"
              className="text-brand-primary/70 hover:text-terracotta transition-colors"
            >
              Terms of Use
            </Link>
            <Link
              to="/"
              data-testid="legal-nav-dashboard"
              className="text-terracotta hover:underline"
            >
              Return to Dashboard
            </Link>
          </nav>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-[10px] uppercase tracking-[0.22em] text-terracotta font-semibold">
          Legal
        </p>
        <h1
          data-testid="legal-page-title"
          className="font-heading text-3xl sm:text-4xl font-semibold mt-1"
        >
          {title}
        </h1>
        <p
          data-testid="legal-page-dates"
          className="text-sm text-brand-primary/60 mt-3"
        >
          <span className="font-medium">Effective:</span> {effectiveDate}
          <span className="mx-2 text-brand-primary/30">·</span>
          <span className="font-medium">Last updated:</span> {lastUpdated}
        </p>

        <article
          data-testid="legal-page-body"
          className="mt-8 text-brand-primary/85 leading-relaxed space-y-4
                     [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-brand-primary [&_h2]:mt-10 [&_h2]:mb-3
                     [&_p]:my-3
                     [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ul]:space-y-1.5
                     [&_li]:leading-relaxed
                     [&_strong]:text-brand-primary [&_strong]:font-semibold
                     [&_a]:text-terracotta [&_a]:underline hover:[&_a]:text-terracotta-hover"
        >
          {children}
        </article>

        <footer className="mt-14 border-t border-brand-primary/10 pt-6 flex items-center justify-between gap-4 flex-wrap text-sm text-brand-primary/60">
          <p>
            &copy; {new Date().getFullYear()} Family Chapel. Internal use only.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/legal/privacy"
              className="hover:text-terracotta transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms"
              className="hover:text-terracotta transition-colors"
            >
              Terms of Use
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
